import { CheckCircle } from '@phosphor-icons/react';
import { ApprovalItem, Button, Heading, Icon, Surface, Text } from '@shh/ui';
import { useState } from 'react';
import { approveTask, decideRedemption, returnTask } from '../data/api';
import { memberById, pendingInstances } from '../data/derive';
import { useFamily } from '../data/family';
import { starsText } from '../i18n/stars';

/** "Wartet auf dich" – tasks and reward requests for parents; decisions stay visible briefly. */
export function Approvals({ hideWhenEmpty = false }: { hideWhenEmpty?: boolean }) {
  const f = useFamily();
  const [decided, setDecided] = useState<
    Record<string, { how: 'approved' | 'returned'; snapshot: Item }>
  >({});
  const [error, setError] = useState<string>();

  type Item = {
    id: string;
    kind: 'task' | 'reward';
    memberId: string;
    what: string;
    points: number;
  };
  const live: Item[] = [
    ...pendingInstances(f).map((i) => {
      const task = f.tasks.find((t) => t.id === i.taskId);
      return {
        id: i.id,
        kind: 'task' as const,
        memberId: i.memberId,
        what: task?.title ?? starsText.task,
        points: task?.points ?? 0,
      };
    }),
    ...f.redemptions.map((r) => ({
      id: r.id,
      kind: 'reward' as const,
      memberId: r.memberId,
      what: f.rewards.find((x) => x.id === r.rewardId)?.title ?? starsText.reward,
      points: r.cost,
    })),
  ];
  const shown = [
    ...live,
    ...Object.values(decided)
      .map((d) => d.snapshot)
      .filter((s) => !live.some((l) => l.id === s.id)),
  ];

  const decide = async (item: Item, how: 'approved' | 'returned') => {
    setError(undefined);
    setDecided((d) => ({ ...d, [item.id]: { how, snapshot: item } }));
    try {
      if (item.kind === 'task')
        await (how === 'approved' ? approveTask(item.id) : returnTask(item.id));
      else await decideRedemption(item.id, how === 'approved');
    } catch (e) {
      setError((e as { message?: string }).message ?? starsText.failedShort);
      setDecided((d) => {
        const next = { ...d };
        delete next[item.id];
        return next;
      });
      return;
    }
    window.setTimeout(
      () =>
        setDecided((d) => {
          const next = { ...d };
          delete next[item.id];
          return next;
        }),
      2500,
    );
  };

  if (hideWhenEmpty && shown.length === 0) return null;
  const pendingTasks = live.filter((i) => i.kind === 'task');

  return (
    <section aria-labelledby="waiting" className="flex flex-col gap-gap-md">
      <div className="flex items-center justify-between gap-gap-md">
        <Heading level={2} size="sm" id="waiting">
          {starsText.waitingForYou}{' '}
          {live.length > 0 && (
            <span className="ml-gap-xs rounded-chip bg-accent-primary px-inset-xs text-sm text-accent-on-accent">
              {live.length}
            </span>
          )}
        </Heading>
        {pendingTasks.length > 1 && (
          <Button
            variant="quiet"
            onPress={() => pendingTasks.forEach((i) => void decide(i, 'approved'))}
          >
            {starsText.approveAll}
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      {shown.length === 0 ? (
        <Surface tone="sunken" className="flex items-center gap-gap-md">
          <Icon icon={CheckCircle} size="lg" weight="fill" className="text-feedback-success" />
          <Text>{starsText.nothingWaiting}</Text>
        </Surface>
      ) : (
        <ul className="flex flex-col gap-gap-md">
          {shown.map((item) => {
            const who = memberById(f, item.memberId);
            return (
              <li key={item.id}>
                <ApprovalItem
                  name={who?.name ?? '?'}
                  color={who?.color ?? 'stone'}
                  emoji={who?.emoji}
                  what={
                    item.kind === 'reward' ? (
                      <>
                        {starsText.wants} <strong>{item.what}</strong>
                      </>
                    ) : (
                      item.what
                    )
                  }
                  points={item.points}
                  kind={item.kind === 'reward' ? 'spend' : 'earn'}
                  returnLabel={item.kind === 'reward' ? starsText.notNow : starsText.again}
                  state={decided[item.id]?.how ?? 'waiting'}
                  onApprove={() => void decide(item, 'approved')}
                  onReturn={() => void decide(item, 'returned')}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
